import { useState, useEffect, useMemo, useCallback } from 'react';
import { BarChart3, PoundSterling } from 'lucide-react';
import { Calendar } from '../components/Calendar';
import { TimeEntryForm } from '../components/TimeEntryForm';
import { ShiftDetails } from '../components/ShiftDetails';
import { MonthlyRateForm } from '../components/MonthlyRateForm';
import { MonthlySummary as MonthlySummaryComponent } from '../components/MonthlySummary';
import { HeatmapWidget } from '../components/widgets/HeatmapWidget';
import { CalendarSkeleton, MonthlySummarySkeleton } from '../components/LoadingSkeleton';
import { AppShell } from '../components/layout/AppShell';
import { PageHeader } from '../components/layout/PageHeader';
import { ClockButton } from '../components/ClockButton';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardBody } from '../components/ui/card';
import { EmptyState } from '../components/ui/empty-state';
import { formatLongDate } from '../lib/format';
import { 
  useTimeEntriesQuery, 
  useAddTimeEntryMutation,
  useUpdateTimeEntryMutation,
  useDeleteTimeEntryMutation 
} from '../hooks/useTimeEntriesQuery';
import { 
  useMonthlySummaryQuery,
  useSetMonthlyRateMutation 
} from '../hooks/useMonthlyRatesQuery';
import { addMonth, subtractMonth, formatDate, calculateHours } from '../utils/dateUtils';
import { TimeEntry } from '../types/database';
import { DateTime } from 'luxon';

export const HomePage: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showTimeEntryForm, setShowTimeEntryForm] = useState(false);
  const [showShiftDetails, setShowShiftDetails] = useState(false);
  const [showRateForm, setShowRateForm] = useState(false);
  const [isClockingIn, setIsClockingIn] = useState(false);

  // Memoize current year and month to prevent unnecessary re-renders
  const currentYear = useMemo(() => currentDate.getFullYear(), [currentDate]);
  const currentMonth = useMemo(() => currentDate.getMonth() + 1, [currentDate]);

  // TanStack Query hooks
  const { 
    data: timeEntries = [], 
    isLoading: entriesLoading,
    error: entriesError 
  } = useTimeEntriesQuery(currentYear, currentMonth);

  const { 
    data: currentSummary, 
    isLoading: summaryLoading 
  } = useMonthlySummaryQuery(currentYear, currentMonth);

  // Mutations
  const addTimeEntryMutation = useAddTimeEntryMutation();
  const updateTimeEntryMutation = useUpdateTimeEntryMutation();
  const deleteTimeEntryMutation = useDeleteTimeEntryMutation();
  const setMonthlyRateMutation = useSetMonthlyRateMutation();

  // Memoized helper function
  const getTimeEntryByDate = useCallback((date: string) => {
    return timeEntries.find(entry => entry.date === date) || null;
  }, [timeEntries]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        setShowTimeEntryForm(false);
        setShowShiftDetails(false);
        setShowRateForm(false);
        setSelectedDate(null);
        console.log('Emergency close all dialogs');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    const existingEntry = getTimeEntryByDate(formatDate(date));
    
    if (existingEntry) {
      setShowShiftDetails(true);
    } else {
      setShowTimeEntryForm(true);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(subtractMonth(currentDate));
    setSelectedDate(null);
    setShowTimeEntryForm(false);
    setShowShiftDetails(false);
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonth(currentDate));
    setSelectedDate(null);
    setShowTimeEntryForm(false);
    setShowShiftDetails(false);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
  };

  const handleSaveTimeEntry = async (entry: Omit<TimeEntry, 'id' | 'created_at' | 'updated_at'>) => {
    const existingEntry = getTimeEntryByDate(entry.date);
    
    try {
      if (existingEntry) {
        await updateTimeEntryMutation.mutateAsync({ id: existingEntry.id!, entry });
      } else {
        await addTimeEntryMutation.mutateAsync(entry);
      }
      
      setShowTimeEntryForm(false);
      setShowShiftDetails(false);
      setSelectedDate(null);
    } catch (error) {
      console.error('Failed to save time entry:', error);
    }
  };

  const handleEditShift = () => {
    setShowShiftDetails(false);
    setShowTimeEntryForm(true);
  };

  const handleDeleteTimeEntry = async (id: number) => {
    try {
      await deleteTimeEntryMutation.mutateAsync(id);
      setShowTimeEntryForm(false);
      setShowShiftDetails(false);
      setSelectedDate(null);
    } catch (error) {
      console.error('Failed to delete time entry:', error);
    }
  };

  const handleSaveRate = async (rate: number) => {
    try {
      await setMonthlyRateMutation.mutateAsync({ 
        year: currentYear, 
        month: currentMonth, 
        hourlyRate: rate 
      });
      setShowRateForm(false);
    } catch (error) {
      console.error('Failed to save monthly rate:', error);
    }
  };

  const handleClockInOut = async () => {
    const nowLondon = DateTime.now().setZone('Europe/London');
    const currentTime = nowLondon.toFormat('HH:mm');
    const todayStr = nowLondon.toFormat('yyyy-MM-dd');
    
    if (isClockingIn) {
      // Clocking out - find today's entry and add clock out time
      const existingEntry = getTimeEntryByDate(todayStr);
      if (existingEntry && existingEntry.shifts) {
        // Find the last shift with clock in but no clock out
        const updatedShifts = [...existingEntry.shifts];
        for (let i = updatedShifts.length - 1; i >= 0; i--) {
          if (updatedShifts[i].clockIn && !updatedShifts[i].clockOut) {
            updatedShifts[i].clockOut = currentTime;
            // Calculate hours for this shift
            const calculatedHours = calculateHours(updatedShifts[i].clockIn, updatedShifts[i].clockOut);
            updatedShifts[i].hours = calculatedHours;
            break;
          }
        }
        
        const totalHours = updatedShifts.reduce((sum, shift) => sum + shift.hours, 0);
        await updateTimeEntryMutation.mutateAsync({
          id: existingEntry.id!,
          entry: {
            date: todayStr,
            shifts: updatedShifts,
            totalHours
          }
        });
        
        // TanStack Query will automatically refresh the summary
      }
      setIsClockingIn(false);
    } else {
      // Clocking in - create or update today's entry
      const existingEntry = getTimeEntryByDate(todayStr);
      let updatedShifts;
      
      if (existingEntry && existingEntry.shifts) {
        updatedShifts = [...existingEntry.shifts];
        // Check if there's an empty shift at the end
        if (updatedShifts.length > 0 && !updatedShifts[updatedShifts.length - 1].clockIn) {
          updatedShifts[updatedShifts.length - 1].clockIn = currentTime;
        } else {
          // Add a new shift
          updatedShifts.push({ clockIn: currentTime, clockOut: '', hours: 0 });
        }
      } else {
        // Create new entry with first shift
        updatedShifts = [{ clockIn: currentTime, clockOut: '', hours: 0 }];
      }
      
      const totalHours = updatedShifts.reduce((sum, shift) => sum + shift.hours, 0);
      
      if (existingEntry) {
        await updateTimeEntryMutation.mutateAsync({
          id: existingEntry.id!,
          entry: {
            date: todayStr,
            shifts: updatedShifts,
            totalHours
          }
        });
      } else {
        await addTimeEntryMutation.mutateAsync({
          date: todayStr,
          shifts: updatedShifts,
          totalHours
        });
      }
      
      // TanStack Query will automatically refresh the summary
      setIsClockingIn(true);
    }
  };

  const selectedTimeEntry = selectedDate ? getTimeEntryByDate(formatDate(selectedDate)) : null;

  return (
    <AppShell
      actions={
        <ClockButton
          isClockedIn={isClockingIn}
          onClick={handleClockInOut}
          disabled={addTimeEntryMutation.isPending || updateTimeEntryMutation.isPending}
        />
      }
    >
      <PageHeader
        eyebrow={formatLongDate(new Date())}
        title="Timesheet"
        description="Log your shifts, keep an eye on hours and see what you've earned this month."
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_360px] xl:grid-cols-[minmax(0,1fr)_390px] 2xl:grid-cols-[minmax(0,1fr)_440px]">
        {/* Calendar */}
        <div className="flex min-w-0 flex-col gap-4">
          {entriesError && (
            <Alert tone="danger">{entriesError.message || 'Failed to load time entries'}</Alert>
          )}

          {entriesLoading ? (
            <CalendarSkeleton />
          ) : (
            <Calendar
              currentDate={currentDate}
              onDateSelect={handleDateSelect}
              onPreviousMonth={handlePreviousMonth}
              onNextMonth={handleNextMonth}
              onToday={handleToday}
              timeEntries={timeEntries}
              selectedDate={selectedDate}
            />
          )}
        </div>

        {/* Sidebar */}
        <aside className="flex flex-col gap-5">
          {summaryLoading ? (
            <MonthlySummarySkeleton />
          ) : currentSummary ? (
            <MonthlySummaryComponent summary={currentSummary} onEditRate={() => setShowRateForm(true)} />
          ) : (
            <Card>
              <CardBody className="pt-5">
                <EmptyState
                  icon={<BarChart3 />}
                  title="No earnings yet"
                  description="Set an hourly rate for this month to start seeing your earnings."
                  action={
                    <Button variant="secondary" size="sm" onClick={() => setShowRateForm(true)}>
                      <PoundSterling />
                      Set hourly rate
                    </Button>
                  }
                />
              </CardBody>
            </Card>
          )}

          <HeatmapWidget
            timeEntries={timeEntries}
            year={currentDate.getFullYear()}
            month={currentDate.getMonth() + 1}
          />
        </aside>
      </div>

      <TimeEntryForm
        selectedDate={selectedDate || new Date()}
        existingEntry={selectedTimeEntry}
        onSave={handleSaveTimeEntry}
        onDelete={handleDeleteTimeEntry}
        isOpen={showTimeEntryForm}
        onClose={() => {
          setShowTimeEntryForm(false);
          setSelectedDate(null);
        }}
      />

      {selectedTimeEntry && (
        <ShiftDetails
          timeEntry={selectedTimeEntry}
          selectedDate={selectedDate || new Date()}
          onEdit={handleEditShift}
          onDelete={handleDeleteTimeEntry}
          isOpen={showShiftDetails}
          onClose={() => {
            setShowShiftDetails(false);
            setSelectedDate(null);
          }}
        />
      )}

      <MonthlyRateForm
        year={currentDate.getFullYear()}
        month={currentDate.getMonth() + 1}
        onSave={handleSaveRate}
        isOpen={showRateForm}
        onClose={() => setShowRateForm(false)}
      />
    </AppShell>
  );
};
